import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Loader2, ExternalLink, CheckCircle2, XCircle, RefreshCw, Unlink, CalendarCheck, CalendarDays, Handshake, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleCalendar = () => {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [syncBookings, setSyncBookings] = useState(true);
  const [syncEvents, setSyncEvents] = useState(true);
  const [syncTrade, setSyncTrade] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const fetchCalendarEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await axios.get(`${API}/calendar/events`);
      setCalendarEvents(res.data.events || []);
    } catch {
      // silently fail
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/settings`);
      setSettings(res.data);
      setClientId(res.data.client_id || '');
      setClientSecret(res.data.client_secret || '');
      setSyncBookings(res.data.sync_bookings ?? true);
      setSyncEvents(res.data.sync_events ?? true);
      setSyncTrade(res.data.sync_trade ?? true);
      if (res.data.connected) {
        fetchCalendarEvents();
      }
    } catch (err) {
      console.error('Failed to fetch calendar settings:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchCalendarEvents]);

  useEffect(() => {
    fetchSettings();
    if (searchParams.get('connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
    }
    if (searchParams.get('error')) {
      toast.error(`Connection failed: ${searchParams.get('error')}`);
    }
  }, [fetchSettings, searchParams]);

  const saveSettings = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.error('Please enter both Client ID and Client Secret');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/calendar/settings`, {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        sync_bookings: syncBookings,
        sync_events: syncEvents,
        sync_trade: syncTrade,
      });
      toast.success('Settings saved!');
      await fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const connectCalendar = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/calendar/connect`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start connection');
      setConnecting(false);
    }
  };

  const disconnectCalendar = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`);
      toast.success('Google Calendar disconnected');
      await fetchSettings();
      setCalendarEvents([]);
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>;
  }

  const isConnected = settings?.connected;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white" data-testid="gcal-title">Google Calendar</h2>
          <p className="text-[#71717A] text-xs">Two-way sync for bookings, events & trade</p>
        </div>
      </div>

      {/* Connection Status */}
      <div className={`mb-6 p-4 rounded-xl border ${isConnected ? 'bg-green-500/5 border-green-500/30' : 'bg-[#0a0a0a] border-[#27272A]'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isConnected ? (
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            ) : (
              <XCircle className="w-5 h-5 text-[#71717A]" />
            )}
            <div>
              <p className={`text-sm font-medium ${isConnected ? 'text-green-400' : 'text-[#A1A1AA]'}`}>
                {isConnected ? 'Connected' : 'Not Connected'}
              </p>
              {isConnected && settings?.google_email && (
                <p className="text-xs text-[#71717A]">{settings.google_email}</p>
              )}
            </div>
          </div>
          {isConnected && (
            <button data-testid="disconnect-btn" onClick={disconnectCalendar}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all">
              <Unlink className="w-3 h-3" /> Disconnect
            </button>
          )}
        </div>
      </div>

      {/* Setup Guide */}
      {!isConnected && (
        <div className="mb-6 p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-[#A1A1AA] space-y-1">
              <p className="text-blue-400 font-medium text-sm">Setup Instructions</p>
              <p>1. Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Google Cloud Console</a> and create a project</p>
              <p>2. Enable <strong>Google Calendar API</strong> under APIs & Services</p>
              <p>3. Configure OAuth Consent Screen (External, add your email as test user)</p>
              <p>4. Create OAuth Credentials (Web application)</p>
              <p>5. Add redirect URI: <code className="text-violet-400 bg-[#1a1a1a] px-1 rounded">{process.env.REACT_APP_PUBLIC_DOMAIN}/api/oauth/calendar/callback</code></p>
              <p>6. Copy Client ID & Secret below</p>
            </div>
          </div>
        </div>
      )}

      {/* Credentials */}
      <div className="space-y-4 mb-6">
        <div>
          <Label className="text-[#EDEDED] text-sm">Google Client ID</Label>
          <Input data-testid="gcal-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)}
            className="mt-1.5 bg-[#121212] border-[#27272A] text-white h-10 text-sm font-mono"
            placeholder="Enter your Google OAuth Client ID" />
        </div>
        <div>
          <Label className="text-[#EDEDED] text-sm">Google Client Secret</Label>
          <Input data-testid="gcal-client-secret" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
            className="mt-1.5 bg-[#121212] border-[#27272A] text-white h-10 text-sm font-mono"
            placeholder="Enter your Google OAuth Client Secret" />
        </div>
      </div>

      {/* Sync Preferences */}
      <div className="mb-6">
        <p className="text-[#EDEDED] text-sm font-medium mb-3">Sync Preferences</p>
        <div className="space-y-3">
          {[
            { id: 'bookings', label: 'Bookings / Appointments', icon: CalendarCheck, value: syncBookings, setter: setSyncBookings, desc: 'Push new bookings to your Google Calendar & block booked slots' },
            { id: 'events', label: 'Events & RSVPs', icon: CalendarDays, value: syncEvents, setter: setSyncEvents, desc: 'Add created events to your Google Calendar' },
            { id: 'trade', label: 'Trade Session Requests', icon: Handshake, value: syncTrade, setter: setSyncTrade, desc: 'Add incoming trade/session requests to your calendar' },
          ].map((item) => (
            <button key={item.id} data-testid={`sync-${item.id}`}
              onClick={() => item.setter(!item.value)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                item.value ? 'bg-violet-500/10 border-violet-500/30' : 'bg-[#0a0a0a] border-[#27272A] hover:border-[#3f3f46]'
              }`}>
              <item.icon className={`w-5 h-5 flex-shrink-0 ${item.value ? 'text-violet-400' : 'text-[#71717A]'}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${item.value ? 'text-white' : 'text-[#A1A1AA]'}`}>{item.label}</p>
                <p className="text-xs text-[#71717A]">{item.desc}</p>
              </div>
              <div className={`w-10 h-6 rounded-full transition-all flex items-center ${item.value ? 'bg-violet-500 justify-end' : 'bg-[#27272A] justify-start'}`}>
                <div className="w-4 h-4 rounded-full bg-white mx-1" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving}
          className="flex-1 h-11 rounded-xl bg-[#1a1a1a] border border-[#27272A] hover:bg-[#222] text-white font-medium">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Settings
        </Button>
        {!isConnected ? (
          <Button data-testid="connect-btn" onClick={connectCalendar} disabled={connecting || !clientId || !clientSecret}
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-500 hover:to-green-500 text-white font-medium">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Connect Calendar
          </Button>
        ) : (
          <Button data-testid="refresh-events-btn" onClick={fetchCalendarEvents} disabled={loadingEvents}
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-500 hover:to-green-500 text-white font-medium">
            {loadingEvents ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh Events
          </Button>
        )}
      </div>

      {/* Calendar Events (when connected) */}
      {isConnected && (
        <div className="mt-6">
          <h3 className="text-white text-sm font-medium mb-3">Upcoming Google Calendar Events</h3>
          {loadingEvents ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>
          ) : calendarEvents.length === 0 ? (
            <p className="text-[#71717A] text-sm text-center py-6">No upcoming events in your calendar</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {calendarEvents.slice(0, 20).map((event) => (
                <div key={event.id} className="p-3 bg-[#0a0a0a] border border-[#27272A] rounded-xl">
                  <p className="text-white text-sm font-medium">{event.summary || 'Untitled'}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-[#71717A]">
                      {event.start ? new Date(event.start).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                    </span>
                    {event.location && (
                      <span className="text-xs text-[#71717A]">{event.location}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleCalendar;
